import { getCurrentUser } from 'vuefire'
import { memberIsAdmin, memberIsBandAdmin, memberIsRedshirtAdmin } from '../shared/utils/memberUtils.js'
import { getRedirectUserIfPresent, useAuthReady } from '~/composables/useAuthReady.js'

const allowedRoutes = ['/login', '/signup', '/logout', '/forgot-password', '/privacy-policy', '/terms-of-service', '/about']

export default defineNuxtRouteMiddleware(async (to: { path: string }, _from: unknown) => {
  if (to.path === '/logout') {
    return true
  }

  // Always wait for auth to be ready - this handles redirect results from Google sign-in
  // and ensures auth state is fully initialized before checking user
  await useAuthReady()

  // If we just completed a redirect sign-in, navigate to home with full page load
  // This ensures clean state initialization after OAuth redirect
  const redirectUser = getRedirectUserIfPresent()
  if (redirectUser && to.path === '/login') {
    return navigateTo('/', { external: true })
  }

  const user = redirectUser ?? await getCurrentUser()

  if (!user) {
    if (!allowedRoutes.includes(to.path)) {
      return navigateTo('/login')
    }
    return true
  }

  // Skip session check for redirect users - we just created the session in useAuthReady
  if (!redirectUser) {
    const { checkSession, refreshSession } = useAuthSession()

    if (!await checkSession()) {
      const refreshed = await refreshSession()
      if (!refreshed) {
        navigateTo('/logout')
        return
      }
    }
  }

  const emailVerified = user.emailVerified ?? true // Default to true for redirect users

  if (to.path === '/verify-email' && user && !emailVerified) {
    return true
  }

  if (user && !emailVerified) {
    return navigateTo('/verify-email')
  }

  const memberData = await useCurrentMember(user.uid)

  if (!memberData) {
    return false
  }

  // Redirect to signup flow if user hasn't completed registration
  if ((!memberData.member_type || memberData.member_type === 'pending') && to.path !== '/signup-flow') {
    return navigateTo('/signup-flow')
  }

  // Check for admin routes
  if (to.path.startsWith('/admin')) {
    // Check if user has admin role
    const admin = memberIsAdmin(memberData)
    const bandAdmin = memberIsBandAdmin(memberData)
    const redshirtAdmin = memberIsRedshirtAdmin(memberData)

    // Special case for /admin/subkrewes - allow band admins
    if (to.path === '/admin/subkrewes') {
      // Allow full admins, band admins, and redshirt admins (limited capabilities enforced in UI/Rules)
      if (!admin && !bandAdmin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into members overview
    else if (to.path === '/admin/members') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into captains overview
    else if (to.path === '/admin/captains') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into lineup (read-only lieutenant assignment)
    else if (to.path === '/admin/lineup') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins and lieutenants into lineup-status (lined-up tracking)
    else if (to.path === '/admin/lineup-status') {
      const isLieutenant = memberData?.isRedshirtLieutenant === true
      if (!admin && !redshirtAdmin && !isLieutenant) {
        return navigateTo('/_error?code=403')
      }
    }
    // Allow redshirt_admins into inventory management
    else if (to.path === '/admin/inventory') {
      if (!admin && !redshirtAdmin) {
        return navigateTo('/_error?code=403')
      }
    }
    else {
      // For all other admin routes, require full admin access
      if (!admin) {
        return navigateTo('/_error?code=403')
      }
    }

    // Only preload data for admin users (including band admins for bands page)
    const { loaded, loadAllData } = useStorePreloader()
    if (!loaded.value) {
      await loadAllData(true)
    }
  }

  return true
})
